import { BaseSelect } from '../BaseSelect/BaseSelect';
import { useRef, FocusEvent } from 'react';
import { useField } from 'formik';
import { FormSelectProps } from './FormSelect.types';
import css from './FormSelect.module.scss';
import { GMMenuItem } from '..';
import { BaseInputLabel } from '../BaseInputLabel/BaseInputLabel';
import { useScrollToOnError } from '../../../helpers/useScrollTo';
import { GMFormControl, GMFormErrorMessage, GMFormWrapper } from '../../Form';

/**
 * The controlled `Select` control that must be used within `Formik`
 * context in order to function.
 *
 * @param id - {@link SelectProps.id id} description.
 * @param name - {@link SelectProps.name name} description.
 * @param label - {@link SelectProps.label label} description.
 * @param customPlaceholder - {@link SelectProps.customPlaceholder customPlaceholder} description.
 * @param children - {@link SelectProps.children children} description.
 */
const FormSelect = ({
    id,
    name,
    label,
    customPlaceholder,
    children,
}: FormSelectProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [field, meta, helper] = useField(name);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    return (
        <GMFormWrapper>
            <GMFormControl>
                {label ? (
                    <BaseInputLabel
                        id={id}
                        classes={{
                            root: css.label,
                            focused: css.focused,
                            error: css.error,
                        }}
                        children={label}
                    />
                ) : null}
                <BaseSelect
                    labelId={id}
                    inputRef={inputRef}
                    classes={{
                        root: css.input,
                        error: css.error,
                        select: css.baseInput,
                    }}
                    name={name}
                    value={field.value ?? ''}
                    error={!!meta.error && meta.touched}
                    onChange={field.onChange}
                    onBlur={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        helper.setTouched(true);
                        field.onBlur(event);
                    }}
                >
                    {customPlaceholder !== undefined ? (
                        <GMMenuItem key={`${name}-customPlaceholder`} value={''}>
                            {customPlaceholder}
                        </GMMenuItem>
                    ) : (
                        children === undefined ? (
                            <GMMenuItem key={`${name}-placeholder`} value={''} />
                        ) : null
                    )}
                    {children}
                </BaseSelect>
                <GMFormErrorMessage name={name} />
            </GMFormControl>
        </GMFormWrapper>
    );
};

export default FormSelect;